import {DataTable} from "../DataTable/DataTable";
import {OperatingAreaEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useOperatingAreaDataTableFilter} from "./useOperatingAreaDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {type ReactNode, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";

const EMPTY_ARRAY: OperatingAreaEntity[] = []

export type BaseOperatingAreaDataTableProps = EntityDataTableProps<OperatingAreaEntity> & {
  renderActions: (record: OperatingAreaEntity, index: number) => ReactNode
}

export const BaseOperatingAreaDataTable = (props: BaseOperatingAreaDataTableProps) => {
  const {
    records: operatingAreas = EMPTY_ARRAY,
    recordsPerPageOptions,
    renderActions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useOperatingAreaDataTableFilter({
    records: operatingAreas
  })

  const sortConfig = useMemo(() => {
    return {
      "name": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.name?.toLowerCase()
          }])
        }
      },
      "companyName": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.companyName?.toLowerCase()
          }])
        }
      },
      "companyUnitName": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.companyUnitName?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const columns = useMemo(() => {
    const values: DataTableColumn<OperatingAreaEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "name"),
        title: t("common.entity.operatingarea.table.column.name", "Name"),
        accessor: 'name',
        width: 100,
        sortable: true,
        render: (record) => (
          <EntityAnchor value={record}/>
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "companyName"),
        title: t("common.entity.operatingarea.table.column.company", "Company"),
        accessor: 'companyName',
        width: 100,
        sortable: true,
      },
      {
        ...getColumnFilterProps(filterConfig, "companyUnitName"),
        title: t("common.entity.operatingarea.table.column.companyunit", "Company Unit"),
        accessor: 'companyUnitName',
        width: 100,
        sortable: true,
      },
      {
        title: '',
        accessor: "actions",
        width: 100,
        textAlignment: 'right',
        render: renderActions,
      }
    ]

    return values
  }, [filterConfig, t, renderActions])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}
